/**
 * 인앱 알림 API (REQ-N, 결정 #9) — 담당자는 `/api/notifications`, 기사는 `/api/driver/notifications`.
 *
 * 외부 발송(알림톡·SMS·푸시)은 범위 밖이다. 화면 안에서만 보여 준다.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { cleanParams } from "@/api/master";
import type { DateString, DateTimeString, NotificationType } from "@/types/domain";

export interface AppNotification {
  id: number;
  notif_type: NotificationType;
  title: string;
  message?: string | null;
  target_role?: string | null;
  target_user_id?: number | null;
  target_driver_id?: number | null;
  ref_type?: string | null;
  ref_id?: number | null;
  /** 참조 회차의 배차일자 — 기사 알림에서 `ref_type='dispatch_trip'` 일 때만 온다 */
  ref_date?: DateString | null;
  is_read: boolean;
  read_at?: DateTimeString | null;
  created_at?: DateTimeString | null;
}

export interface NotificationList {
  items: AppNotification[];
  total: number;
  page: number;
  size: number;
  unread: number;
}

export interface NotificationFilters {
  notif_type?: NotificationType | "";
  is_read?: boolean;
  page?: number;
  size?: number;
}

export const NOTIFICATION_KEY = "notification" as const;

export const notificationKey = {
  root: [NOTIFICATION_KEY] as const,
  list: (scope: "admin" | "driver", filters: NotificationFilters) =>
    [NOTIFICATION_KEY, scope, filters] as const,
};

function basePath(scope: "admin" | "driver"): string {
  return scope === "driver" ? "/driver/notifications" : "/notifications";
}

export async function listNotifications(
  scope: "admin" | "driver",
  filters: NotificationFilters = {}
): Promise<NotificationList> {
  const res = await apiClient.get<NotificationList>(basePath(scope), {
    params: cleanParams({ ...filters }),
  });
  return res.data;
}

/** ID 를 비우면 조회 범위 전체를 읽음으로 표시한다 */
export async function markNotificationsRead(
  scope: "admin" | "driver",
  notificationIds: number[] = []
) {
  const res = await apiClient.post(`${basePath(scope)}/read`, {
    notification_ids: notificationIds,
  });
  return res.data;
}

export function useNotifications(
  scope: "admin" | "driver",
  filters: NotificationFilters = {}
) {
  return useQuery({
    queryKey: notificationKey.list(scope, filters),
    queryFn: () => listNotifications(scope, filters),
    placeholderData: (prev: NotificationList | undefined) => prev,
    // 기사는 폰 브라우저를 켜 둔 채 다른 앱을 오간다 — 돌아왔을 때와 1분마다 새로 받아
    // 담당자가 보낸 미제출 알림이 새로고침 없이 뜨게 한다(푸시가 없으므로 이것이 전부다)
    ...(scope === "driver" ? { refetchOnWindowFocus: true, refetchInterval: 60_000 } : {}),
  });
}

export function useNotificationMutations(scope: "admin" | "driver") {
  const qc = useQueryClient();
  return {
    markRead: useMutation({
      mutationFn: (ids: number[] = []) => markNotificationsRead(scope, ids),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: notificationKey.root });
      },
    }),
  };
}
